import { HStack, Icon, Text, UI, Box, VStack } from '@genial/components';
import { Color, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { SignedInNavigationScreenNavigationProp } from '../../../../../routes/types';
import { reportGeneratorOrderByModalAtom } from '../../../../../store/reportGenerator/filters';
import { useReportGeneratorListing } from '../../../../../store/reportGenerator/listing';
import { DocumentsTable } from '../DocumentsTable';
import { ReportGeneratorListingProps } from '../DocumentsTable/types';

import { DocumentsListingFilterButton } from './shared/DocumentsListingFilterButton';
import { DocumentsListingRefreshButton } from './shared/DocumentsListingRefreshButton';

const LISTING_TITLE_FONT_SIZE = 18;
const LISTING_ACTION_HEIGHT = 36;
const LISTING_ACTION_BORDER_COLOR = Color['brand.primary.principal'];

const styles = StyleSheet.create({
  iconButton: {
    borderWidth: 1,
    borderColor: LISTING_ACTION_BORDER_COLOR,
    borderStyle: 'solid',
    borderRadius: 9999,
    backgroundColor: 'transparent',
    width: LISTING_ACTION_HEIGHT,
    height: LISTING_ACTION_HEIGHT,
    maxHeight: LISTING_ACTION_HEIGHT,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 0,
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    // @ts-expect-error web-only
    boxShadow: 'none',
  },
  iconButtonDisabled: {
    opacity: 0.4,
  },
});

const ListingTitle: React.FC = () => (
  <Text
    variant="bold"
    fontSize={LISTING_TITLE_FONT_SIZE}
    fontWeight={Typography['fontweight.bold']}>
    Histórico
  </Text>
);

const MobileListingActions: React.FC<{ listing: ReportGeneratorListingProps }> = ({ listing }) => {
  const { loading, appliedFiltersCount, onRefresh } = listing;
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { appliedOrderBy } = useRecoilValue(reportGeneratorOrderByModalAtom);

  return (
    <HStack alignItems="center" space="inline.nano" flexShrink={0}>
      <Pressable
        testID="documents-listing-refresh-button"
        onPress={onRefresh}
        disabled={loading}
        style={[styles.iconButton, loading ? styles.iconButtonDisabled : null]}>
        <Icon name="RefreshAlt" size="xxs" color="brand.primary.principal" />
      </Pressable>

      <Pressable
        testID="documents-listing-order-button"
        onPress={() => navigate('ReportGeneratorOrderByModal')}
        style={styles.iconButton}>
        <View>
          <UI.Icon name="Ordering" size="xxs" color="brand.primary.principal" />
          {!!appliedOrderBy && (
            <Box
              w={Typography['fontsize.md']}
              h={Typography['fontsize.md']}
              borderRadius="full"
              background={Color['brand.tertiary.light']}
              position="absolute"
              top={-8}
              right={-8}
            />
          )}
        </View>
      </Pressable>

      <DocumentsListingFilterButton
        appliedFiltersCount={appliedFiltersCount}
        onPress={() => navigate('ReportGeneratorFilterByModal')}
      />
    </HStack>
  );
};

const WideListingActions: React.FC<{ listing: ReportGeneratorListingProps }> = ({ listing }) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const openFilterModal = useCallback(() => {
    navigation.navigate('ReportGeneratorFilterByModal');
  }, [navigation]);

  return (
    <HStack space="inline.xxs" alignItems="center">
      <DocumentsListingRefreshButton isRefreshing={listing.loading} onRefresh={listing.onRefresh} />
      <DocumentsListingFilterButton
        appliedFiltersCount={listing.appliedFiltersCount}
        onPress={openFilterModal}
      />
    </HStack>
  );
};

export const DocumentsListingSection: React.FC = () => {
  const listingState = useReportGeneratorListing();
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const listing: ReportGeneratorListingProps = {
    items: listingState.items,
    loading: listingState.loading,
    error: listingState.error,
    orderBy: listingState.orderBy,
    isLoadingMore: listingState.isLoadingMore,
    appliedFiltersCount: listingState.appliedFilters.length,
    onRefresh: listingState.onRefresh,
    onEndReached: listingState.onEndReached,
    onOrderBy: listingState.onOrderBy,
    onDownload: listingState.onDownload,
    onRetry: listingState.onRetry,
  };

  return (
    <VStack space="stack.xxxs" width="full" flexShrink={0} bg="transparent">
      <HStack
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="full"
        p={isMobile ? 'inline.xxxs' : undefined}>
        <ListingTitle />
        {isMobile ? (
          <MobileListingActions listing={listing} />
        ) : (
          <WideListingActions listing={listing} />
        )}
      </HStack>

      <DocumentsTable listing={listing} />
    </VStack>
  );
};
